// src/CourseContent/Content.jsx

import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { Typography, Breadcrumbs, Link, Box } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { axiosInstance } from "../component/Api/instance";
import { PageHeader } from "../component/ui/PageHeaders";
import { PremiumCard } from "../component/ui/PremiumCard";
import { SkeletonCard } from "../component/ui/Skeletons";
import { styled } from "@mui/material/styles";

const SubjectsPageContainer = styled(Box)(({ theme }) => ({
  minHeight: "85vh",
  padding: "32px 0",
  backgroundColor: theme.palette.background.default,
}));

export default function Content() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planStatus, setPlanStatus] = useState(0);
  const [expiryDate, setExpiryDate] = useState(null);

  const { sno } = useParams();
  const navigate = useNavigate();
  const email = localStorage.getItem("userMail");

  useEffect(() => {
    fetchContent();
  }, [email, sno]);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.post(`get/U_ViewCourseSubject.php`, {
        userId: email,
        courseId: sno,
      });

      if (response.data?.message === "timeout") {
        navigate("/signin");
        return;
      }
      setData(
        Array.isArray(response.data.subjects) ? response.data.subjects : [],
      );

      setPlanStatus(response.data.plan_status ?? 0);
      setExpiryDate(response.data.expiry_date ?? null);
    } catch (error) {
      console.error("Error fetching content:", error);
    } finally {
      setLoading(false);
    }
  };

  const isPlanExpired = () => {
    if (!expiryDate || expiryDate === "0000-00-00") return true;

    const today = new Date();
    const exp = new Date(expiryDate);

    today.setHours(0, 0, 0, 0);
    exp.setHours(0, 0, 0, 0);

    return exp < today;
  };

  const handleSubjectClick = (val) => {
    const expired = isPlanExpired();

    // 🔒 Premium block check
    if (val.ispremium == 1) {
      if (planStatus === 0 || expired) {
        navigate(`/premiumplan/${sno}`);
        return;
      }
    }

    navigate(`/topicview/${val.id}`);
  };

  const isExpired = isPlanExpired();

  return (
    <SubjectsPageContainer>
      <Container>
        {/* Custom Breadcrumb Nav */}
        <Breadcrumbs aria-label="breadcrumb" style={{ marginBottom: "24px" }}>
          <Link underline="hover" color="inherit" href="" onClick={(e) => { e.preventDefault(); navigate("/home"); }}>
            Home
          </Link>
          <Link underline="hover" color="inherit" href="" onClick={(e) => { e.preventDefault(); navigate("/courses"); }}>
            Courses
          </Link>
          <Typography color="text.primary">Subjects</Typography>
        </Breadcrumbs>

        {/* Page Title */}
        <PageHeader 
          title="Course Subjects" 
          subtitle="Explore mock tests and content topics specialized for nursing syllabus areas."
          onBack={() => navigate(-1)}
        />

        <Row className="g-4">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Col key={i} xs={12} sm={6} md={4} lg={3}>
                <SkeletonCard />
              </Col>
            ))
          ) : data.length === 0 ? (
            <Col xs={12} style={{ textAlign: "center", padding: "48px 0" }}>
              <Typography variant="body1" color="textSecondary">
                No subjects found in this course.
              </Typography>
            </Col>
          ) : (
            data.map((val, i) => {
              const isLocked = val.ispremium == 1 && (planStatus === 0 || isExpired);
              return (
                <Col xs={12} sm={6} md={4} lg={3} key={i}>
                  <PremiumCard
                    title={val.sub_name}
                    locked={isLocked}
                    badgeText={val.ispremium == 1 ? "Premium" : "Free"}
                    badgeColor={val.ispremium == 1 ? "var(--color-secondary)" : "var(--color-success)"}
                    onClick={() => handleSubjectClick(val)}
                  >
                    <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '140px', backgroundColor: 'var(--color-surface-hover)', borderRadius: '16px', marginBottom: '12px', padding: '16px' }}>
                      <img
                        src={
                          val.sub_img
                            // ? `https://nursingupdate.in/Nursing/controllers/api/admin/upload/${val.sub_img}`
                            ? `https://nursingupdate.in/testing_be/controllers/api/admin/upload/${val.sub_img}`
                            : ""
                        }
                        alt=""
                        style={{
                          maxHeight: "110px",
                          maxWidth: "100%",
                          objectFit: "contain",
                          borderRadius: "12px",
                        }}
                      />
                    </Box>
                  </PremiumCard>
                </Col>
              );
            })
          )}
        </Row>
      </Container>
    </SubjectsPageContainer>
  );
}